import React, { useEffect, useMemo, useState } from 'react';
import { PickupDataType, PickupType } from '../../../types/shipment';
import { defaultPickupState, getPickupTypeList } from '../../CreateShipment/constants';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import {
  FormHelperText,
  Grid,
  SelectChangeEvent,
  useTheme,
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  alpha,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RadioButton from '@Components/RadioButton';
import DatePicker from '@Components/DatePicker';
import SBWarehouseDropdown from '@Components/Dropdown/Warehouse';
import SBDropdown from '@Components/Dropdown';
import Actions from './Actions';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { updatePickUpAndDropOff } from '@Reducers/AdminShipment';
import { UpdatePickUpAndDropOff } from '@Reducers/AdminShipment/Type';
import { usePickupDropoffAccess } from '@Hook/usePickupDropoffAccess';
import useDebounce from '@Hook/useDebounce';
import { checkPincodeServiceabilityForShipment } from '@Reducers/pincode/actions';
import { isActionAllowed } from '../../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
// import { getWarehouseListForShipment } from '@Reducers/Warehouse';
import { getSpecificWarehouseListForShipment, getWarehouseListForShipment } from '@Reducers/Warehouse/actions';

export default function PickupDropoff() {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isPickupDropoffWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.pick_up_or_drop_off,
    PERFORM_ACTION.write,
  );

  const { shipmentSetting } = useSelector((state: RootStateType) => state?.orgSetup);
  const { pickupTimeSlots } = shipmentSetting || {};

  const { warehouseListForShipmentOptionsHashById } = useSelector((state: RootStateType) => state.Warehouse);
  const countryHashById = useSelector((state: RootStateType) => state.global.countriesObj);
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);

  const [pickupType, setPickupType] = useState<PickupDataType>({ ...defaultPickupState });
  const [timeSlotsOption, setTimeSlotsOption] = useState<DropdownOptionType[]>([]);

  const [error, setError] = useState<string>('');

  const { disablePickup } = usePickupDropoffAccess({
    shipperCountry: shipmentById?._shipperId.country,
  });
  const shipperPincode = shipmentById?._shipperId.pincode;

  const debouncedSend = useDebounce(({ pincode, pickupType }: { pincode: string; pickupType: string }) => {
    if (pincode && pickupType === 'pickUp') dispatch(checkPincodeServiceabilityForShipment({ pincode }));
    if (pickupType !== 'pickUp')
      dispatch(getSpecificWarehouseListForShipment(shipmentById?.accountNumber ? shipmentById?.accountNumber : ''));
    if (pickupType !== 'pickUp') {
      setTimeout(() => {
        dispatch(getWarehouseListForShipment(pincode ? { pincode } : {}));
      }, 1000);
    }
  }, 1000);

  useEffect(() => {
    // if (pickupType.type === 'pickUp')
    debouncedSend({ pincode: shipperPincode ? shipperPincode : '', pickupType: pickupType.type });
  }, [shipperPincode, pickupType.type]);

  useEffect(() => {
    const _packageId = shipmentById?._packageId;
    if (_packageId) {
      const warehouseId = shipmentById._warehouseId;
      const isDropOff = _packageId.isDropOff;
      const timeSlot = _packageId?.timeSlot;
      const pickupDate = _packageId?.pickupDate;
      const date = pickupDate ? dayjs(pickupDate) : dayjs();
      setPickupType({
        ...pickupType,
        type: isDropOff ? 'dropOff' : 'pickUp',
        date,
        timeSlot: timeSlot,
        warehouse: warehouseId || '',
      });
    }
  }, [shipmentById?._packageId]);

  useEffect(() => {
    const slots = pickupTimeSlots?.slots;
    if (slots) {
      setTimeSlotsOption(
        slots.map(({ from, to }) => {
          const isToday = dayjs(pickupType.date).isSame(dayjs(), 'day');
          const checkPastTime = dayjs(to, 'hh a').isBefore(dayjs(dayjs(), 'hh:mm a'));
          const str = `${from} - ${to}`;
          return { display: str, value: str, disable: isToday && checkPastTime };
        }),
      );
    }
  }, [pickupTimeSlots, pickupType.date]);

  const handlePickupTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === 'dropOff') {
      setPickupType({ ...defaultPickupState, type: value as PickupType, warehouse: shipmentById?._warehouseId || '' });
    } else {
      setPickupType({ ...defaultPickupState, type: value as PickupType });
    }
    setError('');
  };

  const handleTimeSlotChange = (
    _selected: Array<string>,
    _selectedObj: DropdownOptionType[],
    event: SelectChangeEvent<string>,
  ) => {
    const value = event.target.value;
    setPickupType({ ...pickupType, timeSlot: value });
  };

  const handlePickupDateChange = (date: Dayjs) => {
    setPickupType({ ...pickupType, date: date });
  };

  const handleWarehouseChange = (
    [_selected]: Array<string>,
    _selectedObj: DropdownOptionType[],
    _event: SelectChangeEvent<string>,
  ) => {
    const value = _selected;
    setPickupType({ ...pickupType, warehouse: value });
  };

  const onSava = () => {
    const isDropOff = pickupType.type === 'dropOff';
    const slots = pickupTimeSlots?.slots;
    if (!isDropOff && (!pickupType.date || (slots && slots?.length > 0 && !pickupType.timeSlot))) {
      return setError('Pickup date and time is required.');
    } else if (isDropOff && !pickupType.warehouse) {
      return setError('Drop of warehouse is required.');
    }
    error && setError('');
    if (!shipmentById) return;
    const payload: UpdatePickUpAndDropOff = { shipmentNumber: shipmentById.shipmentNumber, isDropOff: isDropOff };
    if (isDropOff && pickupType.warehouse) {
      const selectedWarehouse = warehouseListForShipmentOptionsHashById[pickupType.warehouse];
      const coAttention = selectedWarehouse.coAttention;
      const [telephoneCode, number] = coAttention.contact;
      payload.warehouseId = pickupType.warehouse;
      const countryId = selectedWarehouse._addressId.country;
      payload.warehouseAddress = {
        ...selectedWarehouse._addressId,
        country: countryHashById?.[countryId]?.name,
        name: selectedWarehouse.name,
        phone: { telephoneCode, number, name: coAttention.name },
        email: coAttention.email,
      };
    } else if (pickupType.date) {
      payload.pickupDate = pickupType.date.toISOString();
      if (slots && slots?.length > 0 && pickupType.timeSlot) {
        payload.timeSlot = pickupType.timeSlot;
      }
    }

    dispatch(updatePickUpAndDropOff(payload));
  };
  const pickupTypeList = useMemo(() => getPickupTypeList(name), [name]);

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: '14px',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        bgcolor: theme.palette.background.paper,
        overflow: 'hidden',
        mb: 3,
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LocalShippingOutlinedIcon />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
              {name === 'Cargo Solutions Network' ? 'Drop-off Warehouse' : 'Dispatch Method'}
            </Typography>
            <Chip
              label={pickupType.type === 'dropOff' ? 'Drop-off' : 'Pickup'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
            Choose whether the carrier collects from the shipper or you drop off at a hub
          </Typography>
        </Box>
      </Box>

      {/* Card Content */}
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item md={pickupType.type === 'dropOff' ? 5 : 4} xs={12}>
            <RadioButton
              containerClass="w-full"
              name="type"
              value={pickupType.type}
              onChange={handlePickupTypeChange}
              list={pickupTypeList}
              sx={{ display: 'flex', columnGap: 4, flexWrap: 'wrap', rowGap: 1 }}
            />
          </Grid>
          {pickupType.type === 'dropOff' && (
            <Grid item md={7} xs={12}>
              <SBWarehouseDropdown
                value={pickupType.warehouse || ''}
                onChange={handleWarehouseChange}
                disabled={name === 'Cargo Solutions Network' ? true : !isPickupDropoffWriteAllowed}
              />
              {error && <FormHelperText error>{error}</FormHelperText>}
            </Grid>
          )}
          {pickupType.type === 'pickUp' && (
            <>
              <Grid item md={timeSlotsOption?.length ? 4 : 8} sm={6} xs={12}>
                <DatePicker
                  minDate={shipmentById?.createdAt ? dayjs(shipmentById?.createdAt) : dayjs()}
                  label="Date"
                  value={pickupType.date}
                  handleChange={handlePickupDateChange}
                  disabled={
                    !isPickupDropoffWriteAllowed
                      ? !isPickupDropoffWriteAllowed
                      : shipperPincode
                      ? !!disablePickup
                      : false
                  }
                />
              </Grid>
              {timeSlotsOption?.length ? (
                <Grid item md={4} sm={6} xs={12}>
                  <SBDropdown
                    label={'Time Slot'}
                    id="timeSlot"
                    name="timeSlot"
                    isSortingAllowed={false}
                    placeholder="Select time slot"
                    options={timeSlotsOption}
                    value={pickupType.timeSlot ? [pickupType.timeSlot] : []}
                    onChange={handleTimeSlotChange}
                    disabled={
                      !isPickupDropoffWriteAllowed
                        ? !isPickupDropoffWriteAllowed
                        : shipperPincode
                        ? !!disablePickup
                        : false
                    }
                  />
                  {error && <FormHelperText error>{error}</FormHelperText>}
                </Grid>
              ) : null}
            </>
          )}
        </Grid>

        <Actions disabled={name === 'Cargo Solutions Network' ? true : !isPickupDropoffWriteAllowed} onSave={onSava} />
      </CardContent>
    </Card>
  );
}
